import React, { useState, useRef, useEffect } from 'react';
import { formatNomenclatureWithSize } from '@equip/utils';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  type BoxProps,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Notes as NotesIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import type { OrderRow } from '@/pages/PurchaseRequisitionsPage';
import type { OrderLineItemRow } from '@/services/ordersService';
import { getStatusChipProps } from '@/utils/orderStatusColors';
import { useUpdateEntity, useEntityActivity, useOrderLineItemsHistory } from '@/hooks';
import type { OrderLineItemHistoryRow } from '@/services/ordersService';
import { useNotifications } from '@/stores/useNotificationStore';
import { getErrorMessage } from '@/utils/httpClient';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';
import { formatCurrency } from '@/utils/currency';
import { STATUS_OPTIONS } from '@/constants/orderConstants';
import dayjs from 'dayjs';
import { useModuleConfig } from '@/hooks/queries/admin/useModuleConfig';
import type { SupplyModuleConfig } from '@/types/moduleConfig';

interface ExpandedOrderContentProps {
  order: OrderRow;
  lineItems: OrderLineItemRow[];
}

const getTotalReceived = (item: OrderLineItemRow): number => {
  return (
    item.receipts?.reduce(
      (sum, receipt) => sum + (receipt.entity_data?.quantity_received ?? 0),
      0,
    ) ?? 0
  );
};

const cellSx = {
  padding: '12px',
  verticalAlign: 'top',
  borderBottom: '1px solid',
  borderColor: 'divider',
} as const;

interface CancelledLineItemRowProps {
  lineItem: OrderLineItemHistoryRow;
  rowIndex: number;
  activeRowCount: number;
}

const CancelledLineItemRow: React.FC<CancelledLineItemRowProps> = ({
  lineItem,
  rowIndex,
  activeRowCount,
}) => {
  const quantity = lineItem.entity_data.quantity ?? 0;
  const unitPrice = lineItem.entity_data.unit_price_at_time_of_order ?? 0;
  const actualPrice = lineItem.entity_data.actual_price ?? unitPrice;
  const lineTotal = quantity * actualPrice;
  const cancelledDate = lineItem.cancelled_at
    ? new Date(lineItem.cancelled_at).toLocaleDateString('en-US')
    : '-';
  const contractNumber = lineItem.entity_data.contract_number;

  return (
    <TableRow
      sx={{
        bgcolor: (t) =>
          (activeRowCount + rowIndex) % 2 === 0
            ? 'background.paper'
            : t.palette.mode === 'dark'
              ? 'grey.800'
              : 'grey.50',
        opacity: 0.65,
      }}
    >
      {/* Date (cancelled date) */}
      <TableCell sx={cellSx}>
        <Typography sx={{ fontSize: '13px', fontWeight: 400, textDecoration: 'line-through' }}>
          {cancelledDate}
        </Typography>
      </TableCell>
      {/* LIN */}
      <TableCell sx={cellSx}>
        <Typography sx={{ fontSize: '13px', fontWeight: 400, textDecoration: 'line-through' }}>
          {lineItem.catalog?.entity_data?.lin || '-'}
        </Typography>
      </TableCell>
      {/* NSN/MFR PN */}
      <TableCell sx={cellSx}>
        <Typography sx={{ fontSize: '13px', fontWeight: 400, textDecoration: 'line-through' }}>
          {lineItem.catalog?.entity_data?.nsn || '-'}
        </Typography>
      </TableCell>
      {/* Nomenclature */}
      <TableCell sx={{ ...cellSx, maxWidth: '250px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 400,
              lineHeight: '1.5',
              whiteSpace: 'normal',
              textDecoration: 'line-through',
            }}
          >
            {formatNomenclatureWithSize(
              lineItem.catalog?.entity_data?.nomenclature ||
                lineItem.catalog?.display_name ||
                lineItem.display_name ||
                '-',
              lineItem.catalog?.entity_data?.size as string | undefined,
            )}
          </Typography>
          <Chip
            label="Canceled"
            size="small"
            sx={{
              bgcolor: 'error.main',
              color: 'common.white',
              fontSize: '10px',
              fontWeight: 600,
              height: '18px',
              alignSelf: 'flex-start',
            }}
          />
        </Box>
      </TableCell>
      {/* Qty Ordered */}
      <TableCell align="right" sx={cellSx}>
        <Typography
          sx={{
            fontFamily: 'Roboto Mono',
            fontSize: '13px',
            fontWeight: 400,
            textDecoration: 'line-through',
          }}
        >
          {quantity}
        </Typography>
      </TableCell>
      {/* Line Total */}
      <TableCell align="right" sx={cellSx}>
        <Typography
          sx={{
            fontFamily: 'Roboto Mono',
            fontSize: '13px',
            fontWeight: 500,
            textDecoration: 'line-through',
          }}
        >
          {formatCurrency(lineTotal)}
        </Typography>
      </TableCell>
      {/* Qty Received */}
      <TableCell align="right" sx={cellSx}>
        <Typography
          sx={{
            fontFamily: 'Roboto Mono',
            fontSize: '13px',
            fontWeight: 400,
            color: 'text.secondary',
          }}
        >
          —
        </Typography>
      </TableCell>
      {/* Due In */}
      <TableCell align="right" sx={cellSx}>
        <Typography
          sx={{
            fontFamily: 'Roboto Mono',
            fontSize: '13px',
            fontWeight: 400,
            color: 'text.secondary',
          }}
        >
          —
        </Typography>
      </TableCell>
      {/* Date Received */}
      <TableCell sx={cellSx}>
        <Typography sx={{ fontSize: '13px', fontWeight: 400, color: 'text.secondary' }}>
          —
        </Typography>
      </TableCell>
      {/* Contract Number (read-only for cancelled) */}
      <TableCell sx={cellSx}>
        <Typography sx={{ fontSize: '13px', fontWeight: 400, textDecoration: 'line-through' }}>
          {contractNumber || '—'}
        </Typography>
      </TableCell>
      {/* Notes: show cancellation reason (read-only) */}
      <TableCell sx={{ ...cellSx, maxWidth: '200px' }}>
        {lineItem.cancellation_reason ? (
          <Tooltip title={lineItem.cancellation_reason}>
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 400,
                color: 'error.main',
                whiteSpace: 'normal',
                maxWidth: '150px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {lineItem.cancellation_reason}
            </Typography>
          </Tooltip>
        ) : (
          <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>—</Typography>
        )}
      </TableCell>
    </TableRow>
  );
};

export const ExpandedOrderContent: React.FC<ExpandedOrderContentProps> = ({ order, lineItems }) => {
  const isViewer = useWriteGuard();
  const updateEntity = useUpdateEntity();
  const notifications = useNotifications();
  const queryClient = useQueryClient();
  const contentRef = useRef<HTMLDivElement>(null);

  /** Builds viewer-aware interactive props for an editable cell/region. */
  const editableProps = (
    onClick: () => void,
    ariaLabel: string,
    baseSx?: BoxProps['sx'],
  ): BoxProps =>
    isViewer
      ? {
          sx: { cursor: 'default', ...baseSx },
        }
      : {
          onClick,
          role: 'button',
          tabIndex: 0,
          'aria-label': ariaLabel,
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          },
          sx: {
            cursor: 'pointer',
            '&:hover .edit-icon': { opacity: 1 },
            ...baseSx,
          },
        };

  // Fetch soft-deleted (fully-cancelled) line items for history display
  const lineItemsHistoryQuery = useOrderLineItemsHistory(order.id);
  const cancelledLineItems: OrderLineItemHistoryRow[] = lineItemsHistoryQuery.data ?? [];

  // Fetch the last activity for this order
  const activityQuery = useEntityActivity({
    entityId: order.id,
    limit: 1,
    refetchInterval: 5000, // Real-time updates every 5 seconds
  });
  const [expandedSections, setExpandedSections] = useState<{
    summary: boolean;
    lineItems: boolean;
  }>({
    summary: true,
    lineItems: false,
  });
  const [editingCell, setEditingCell] = useState<{ lineItemId: string; field: string } | null>(
    null,
  );
  const [tempValues, setTempValues] = useState<Record<string, unknown>>({});
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [editingNotes, setEditingNotes] = useState<{
    lineItemId: string;
    currentNotes: string;
  } | null>(null);
  const [notesValue, setNotesValue] = useState('');

  // Reset state when order changes to prevent state bleeding between orders
  useEffect(() => {
    setExpandedSections({
      summary: true,
      lineItems: false,
    });
    setEditingCell(null);
    setTempValues({});
    setNotesModalOpen(false);
    setEditingNotes(null);
    setNotesValue('');
  }, [order.id]);

  // Cleanup tempValues on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      setTempValues({});
    };
  }, []);

  useEffect(() => {
    if (expandedSections.lineItems && contentRef.current) {
      // Find the parent dialog content element and scroll it to bottom
      const dialogContent = contentRef.current.closest('.MuiDialogContent-root');
      if (dialogContent) {
        // Small delay to ensure accordion is fully expanded
        setTimeout(() => {
          dialogContent.scrollTo({ top: dialogContent.scrollHeight, behavior: 'smooth' });
        }, 300);
      }
    }
  }, [expandedSections.lineItems]);

  const toggleSection = (section: 'summary' | 'lineItems') => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleUpdateExternalStatus = (newStatus: string) => {
    const ordersQueryKey = queryKeys.orders.list();

    // Derive internal status from external status to keep them in sync (AB#29366)
    // Use exact-value matching to prevent false positives (e.g. "Natick/MIPR completed"
    // should NOT set internal status to Completed — AB#29102 regression fix).
    const externalToInternalStatus = (ext: string): string | undefined => {
      const map: Record<string, string> = {
        'completed/received': 'Completed',
        cancelled: 'Cancelled',
        partial: 'Partially Received',
      };
      return map[ext.toLowerCase()] ?? undefined;
    };
    const derivedInternalStatus = externalToInternalStatus(newStatus);

    updateEntity.mutate(
      {
        type: 'order',
        id: order.id,
        patch: {
          entity_data: {
            external_status: newStatus,
            status_date: new Date().toISOString(),
            ...(derivedInternalStatus ? { status: derivedInternalStatus } : {}),
          },
        },
      },
      {
        onSuccess: () => {
          notifications.success('Status Updated', 'Order status has been updated successfully');

          // Directly update the cache with the new external status
          queryClient.setQueryData(ordersQueryKey, (oldData: OrderRow[] | undefined) => {
            if (!oldData) return oldData;
            return oldData.map((item) =>
              item.id === order.id
                ? {
                    ...item,
                    ...(derivedInternalStatus ? { status: derivedInternalStatus } : {}),
                    entity_data: {
                      ...(item.entity_data ?? {}),
                      external_status: newStatus,
                      status_date: new Date().toISOString(),
                      ...(derivedInternalStatus ? { status: derivedInternalStatus } : {}),
                    },
                  }
                : item,
            );
          });
        },
        onError: (error) => {
          const errorMessage = getErrorMessage(error);
          notifications.error('Update Failed', errorMessage);
          // Log error for debugging
          console.error('Failed to update order external status:', error);
        },
      },
    );
  };

  const handleStartEdit = (lineItemId: string, field: string, currentValue: unknown) => {
    setEditingCell({ lineItemId, field });
    setTempValues({ ...tempValues, [`${lineItemId}-${field}`]: currentValue });
  };

  const handleSaveEdit = (lineItemId: string, field: string) => {
    const key = `${lineItemId}-${field}`;
    const value = tempValues[key];
    const orderId = order.id;
    const lineItemsQueryKey = queryKeys.orders.lineItems(orderId);

    updateEntity.mutate(
      {
        type: 'order_line_item',
        id: lineItemId,
        patch: {
          entity_data: {
            [field]: value,
          },
        },
      },
      {
        onSuccess: () => {
          const fieldLabel = field === 'contract_number' ? 'Contract number' : field;
          notifications.success('Changes Saved', `${fieldLabel} has been updated successfully`);
          setEditingCell(null);

          // Clean up tempValues to prevent memory leak
          setTempValues((prev) => {
            const { [key]: _, ...rest } = prev;
            return rest;
          });

          // Directly update the cache with the new value
          queryClient.setQueryData(lineItemsQueryKey, (oldData: OrderLineItemRow[] | undefined) => {
            if (!oldData) return oldData;
            return oldData.map((item) =>
              item.id === lineItemId
                ? {
                    ...item,
                    entity_data: {
                      ...item.entity_data,
                      [field]: value,
                    },
                  }
                : item,
            );
          });
        },
        onError: (error) => {
          const errorMessage = getErrorMessage(error);
          notifications.error('Save Failed', errorMessage);
          // Log error for debugging
          console.error('Failed to update line item:', error);
        },
      },
    );
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    // Clean up tempValues for cancelled edit to prevent memory leak
    if (editingCell) {
      const key = `${editingCell.lineItemId}-${editingCell.field}`;
      setTempValues((prev) => {
        const { [key]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleTempValueChange = (lineItemId: string, field: string, value: unknown) => {
    const key = `${lineItemId}-${field}`;
    setTempValues({ ...tempValues, [key]: value });
  };

  const handleOpenNotesModal = (lineItemId: string, currentNotes: string) => {
    setEditingNotes({ lineItemId, currentNotes });
    setNotesValue(currentNotes);
    setNotesModalOpen(true);
  };

  const handleCloseNotesModal = () => {
    setNotesModalOpen(false);
    setEditingNotes(null);
    setNotesValue('');
  };

  const handleSaveNotes = () => {
    if (editingNotes) {
      const lineItemId = editingNotes.lineItemId;
      const orderId = order.id;
      const lineItemsQueryKey = queryKeys.orders.lineItems(orderId);

      updateEntity.mutate(
        {
          type: 'order_line_item',
          id: lineItemId,
          patch: {
            entity_data: {
              notes: notesValue,
            },
          },
        },
        {
          onSuccess: () => {
            notifications.success('Notes Saved', 'Your notes have been saved successfully');
            handleCloseNotesModal();

            // Directly update the cache with the new notes value
            queryClient.setQueryData(
              lineItemsQueryKey,
              (oldData: OrderLineItemRow[] | undefined) => {
                if (!oldData) return oldData;
                return oldData.map((item) =>
                  item.id === lineItemId
                    ? {
                        ...item,
                        entity_data: {
                          ...item.entity_data,
                          notes: notesValue,
                        },
                      }
                    : item,
                );
              },
            );
          },
          onError: (error) => {
            notifications.error('Save Failed', getErrorMessage(error));
          },
        },
      );
    }
  };

  const calculateGrandTotal = () => {
    return lineItems.reduce((sum, item) => {
      const quantity = item.entity_data.quantity ?? 0;
      const unitPrice = item.entity_data.unit_price_at_time_of_order ?? 0;
      const actualPrice = item.entity_data.actual_price ?? unitPrice;
      return sum + quantity * actualPrice;
    }, 0);
  };

  const purchaseType = order.entity_data?.purchase_type || 'N/A';
  const ufr = order.entity_data?.ufr || false;
  const externalStatus = order.entity_data?.external_status || '';
  const description = order.entity_data?.description || '-';
  const documentNumber = order.entity_data?.document_number || '-';
  const logFormNumber = order.entity_data?.log_form_number;
  const { config: supplyConfig } = useModuleConfig<SupplyModuleConfig>('supply');
  const assetAuthorityLabel = supplyConfig.features.purchase_requisitions.asset_authority_label;
  const assetAuthority = (order.entity_data?.asset_authority as string) || '-';
  const priority = (order.entity_data?.priority as string) || '-';
  const vendor = (order.entity_data?.vendor as string) || '-';
  const expectedDeliveryDate = order.entity_data?.expected_delivery_date
    ? new Date(order.entity_data.expected_delivery_date as string).toLocaleDateString('en-US')
    : '-';
  const requiredByDate = order.entity_data?.required_by_date
    ? new Date(order.entity_data.required_by_date as string).toLocaleDateString('en-US')
    : '-';
  const fmz = (order.entity_data?.fmz as string) || '-';
  const wbsCc = (order.entity_data?.wbs_cc as string) || '-';
  const eQuipStatus = order.status;
  // Don't fallback to orderDate - status_date should be explicit or show as "Not Set"
  const statusDate = order.entity_data?.status_date
    ? new Date(order.entity_data.status_date).toLocaleDateString('en-US')
    : 'Not Set';

  const renderEditableCell = (
    lineItem: OrderLineItemRow,
    field: 'contract_number',
    currentValue: string | undefined,
  ) => {
    const isEditing = editingCell?.lineItemId === lineItem.id && editingCell?.field === field;
    const key = `${lineItem.id}-${field}`;
    const displayValue = currentValue || '—';
    const isSaving =
      updateEntity.isPending &&
      editingCell?.lineItemId === lineItem.id &&
      editingCell?.field === field;

    if (isEditing) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            type="text"
            value={(tempValues[key] as string) ?? currentValue ?? ''}
            onChange={(e) => handleTempValueChange(lineItem.id, field, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSaveEdit(lineItem.id, field);
              } else if (e.key === 'Escape') {
                handleCancelEdit();
              }
            }}
            disabled={isSaving}
            autoFocus
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '13px',
                padding: '8px',
              },
            }}
          />
          <IconButton
            size="small"
            onClick={() => handleSaveEdit(lineItem.id, field)}
            disabled={isSaving}
            aria-label="Save changes"
          >
            {isSaving ? (
              <Box
                sx={{
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid',
                    borderColor: 'primary.main',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                    '@keyframes spin': {
                      from: { transform: 'rotate(0deg)' },
                      to: { transform: 'rotate(360deg)' },
                    },
                  }}
                />
              </Box>
            ) : (
              <SaveIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      );
    }

    const label = `Edit ${field === 'contract_number' ? 'contract number' : field}`;
    const props = editableProps(() => handleStartEdit(lineItem.id, field, currentValue), label, {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
    });

    return (
      <Box {...props}>
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 400,
          }}
        >
          {displayValue}
        </Typography>
        <EditIcon
          className="edit-icon"
          sx={{
            fontSize: '14px',
            opacity: 0,
            transition: 'opacity 0.2s',
            color: 'primary.main',
          }}
        />
      </Box>
    );
  };

  const SummaryRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box
      sx={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid', borderColor: 'divider' }}
    >
      <Typography
        sx={{
          fontSize: '13px',
          fontWeight: 400,
          color: 'text.secondary',
          minWidth: '180px',
        }}
      >
        {label}:
      </Typography>
      <Box
        sx={{
          fontSize: '13px',
          fontWeight: 400,
          flex: 1,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {value}
      </Box>
    </Box>
  );

  const statusChipProps = getStatusChipProps(externalStatus || order.status);

  // Get the last activity entry
  const lastActivity = activityQuery.data?.[0];

  // Format field changes for display
  const formatFieldChange = (field: string): string => {
    return field
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return value.toLocaleString();
    if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
      return new Date(value).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const getFieldChanges = () => {
    if (!lastActivity || lastActivity.operation === 'INSERT') return null;

    const oldEntityData = (lastActivity.old_data?.entity_data as Record<string, unknown>) || {};
    const newEntityData = (lastActivity.new_data?.entity_data as Record<string, unknown>) || {};
    const changes: Array<{ field: string; oldValue: unknown; newValue: unknown }> = [];

    // Get all changed fields
    const allKeys = new Set([...Object.keys(oldEntityData), ...Object.keys(newEntityData)]);
    const ignoredFields = new Set(['updated_at', 'created_at', 'version', 'metadata']);

    for (const key of allKeys) {
      if (ignoredFields.has(key) || key.startsWith('_')) continue;

      const oldValue = oldEntityData[key];
      const newValue = newEntityData[key];
      const oldNormalized = oldValue === undefined ? null : oldValue;
      const newNormalized = newValue === undefined ? null : newValue;

      if (JSON.stringify(oldNormalized) !== JSON.stringify(newNormalized)) {
        changes.push({ field: key, oldValue, newValue });
      }
    }

    // Check top-level fields
    const topLevelFields = ['display_name', 'entity_identifier', 'status'];
    for (const field of topLevelFields) {
      const oldValue = lastActivity.old_data?.[field];
      const newValue = lastActivity.new_data?.[field];
      const oldNormalized = oldValue === undefined ? null : oldValue;
      const newNormalized = newValue === undefined ? null : newValue;

      if (JSON.stringify(oldNormalized) !== JSON.stringify(newNormalized)) {
        changes.push({ field, oldValue, newValue });
      }
    }

    return changes.length > 0 ? changes : null;
  };

  return (
    <Box ref={contentRef} sx={{ bgcolor: 'background.paper' }}>
      {/* Header with status chips */}
      <Box
        sx={{
          padding: '16px 24px',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Chip
            label={externalStatus || order.status}
            sx={{
              backgroundColor: statusChipProps.backgroundColor,
              color: statusChipProps.color,
              fontSize: '12px',
              fontWeight: 500,
              height: '28px',
            }}
          />
          {purchaseType !== 'N/A' && (
            <Chip
              label={purchaseType}
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontSize: '12px',
                fontWeight: 500,
                height: '28px',
              }}
            />
          )}
        </Box>
      </Box>

      {/* Requisition Summary Section */}
      <Accordion
        expanded={expandedSections.summary}
        onChange={() => toggleSection('summary')}
        sx={{
          boxShadow: 'none',
          '&:before': { display: 'none' },
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            padding: '0 24px',
            minHeight: '56px',
            '&.Mui-expanded': { minHeight: '56px' },
            bgcolor: (t) => (t.palette.mode === 'dark' ? 'grey.800' : 'grey.50'),
            borderBottom: expandedSections.summary ? '1px solid' : 'none',
            borderColor: 'divider',
          }}
        >
          <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>Requisition Summary</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: '16px 24px', bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 4 }}>
            <SummaryRow
              label="Log Form #"
              value={logFormNumber !== undefined ? String(logFormNumber).padStart(6, '0') : '-'}
            />
            <SummaryRow label="Document Number" value={documentNumber} />
            <SummaryRow label="Description" value={description} />
            <SummaryRow label="Purchase Type" value={purchaseType} />
            <SummaryRow label={assetAuthorityLabel} value={assetAuthority} />
            <SummaryRow label="Priority" value={priority} />
            <SummaryRow label="Vendor" value={vendor} />
            <SummaryRow label="FMZ" value={fmz} />
            <SummaryRow label="Expected Delivery Date" value={expectedDeliveryDate} />
            <SummaryRow label="Required By Date" value={requiredByDate} />
            <SummaryRow label="WBS / CC" value={wbsCc} />
            <SummaryRow
              label="Status"
              value={
                <Chip
                  label={externalStatus || order.status}
                  size="small"
                  sx={{
                    backgroundColor: statusChipProps.backgroundColor,
                    color: statusChipProps.color,
                    fontSize: '12px',
                    fontWeight: 500,
                    height: '24px',
                  }}
                />
              }
            />
            <SummaryRow label="Status Date" value={statusDate} />
            <SummaryRow label="eQuip Status" value={eQuipStatus} />
            <SummaryRow
              label="Grand Total"
              value={
                <Typography
                  sx={{
                    fontFamily: 'Roboto Mono',
                    fontSize: '14px',
                    fontWeight: 700,
                    color: 'success.main',
                  }}
                >
                  {formatCurrency(calculateGrandTotal())}
                </Typography>
              }
            />
            <SummaryRow
              label="UFR Status"
              value={
                <Chip
                  label={ufr ? 'Yes - Unfunded Requirement' : 'No'}
                  size="small"
                  sx={{
                    bgcolor: (t) =>
                      ufr ? 'error.main' : t.palette.mode === 'dark' ? 'grey.600' : 'grey.400',
                    color: 'common.white',
                    fontSize: '12px',
                    fontWeight: 500,
                    height: '24px',
                  }}
                />
              }
            />
          </Box>

          {/* Last Activity Section */}
          <Box
            sx={{
              marginTop: '16px',
              paddingTop: '16px',
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            {activityQuery.isLoading ? (
              <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>
                Loading activity...
              </Typography>
            ) : activityQuery.error ? (
              <Typography sx={{ fontSize: '13px', color: 'error.main' }}>
                Failed to load activity
              </Typography>
            ) : !lastActivity ? (
              <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>
                No activity recorded
              </Typography>
            ) : (
              <Box
                sx={{
                  bgcolor: (t) => (t.palette.mode === 'dark' ? 'grey.800' : 'grey.50'),
                  padding: '12px',
                  borderRadius: '4px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '13px',
                    marginBottom: '8px',
                  }}
                >
                  <Box
                    component="span"
                    sx={{ fontWeight: 500, color: 'text.secondary', marginRight: '16px' }}
                  >
                    Last Activity:
                  </Box>
                  <Box component="span" sx={{ fontWeight: 500 }}>
                    {lastActivity.operation === 'INSERT'
                      ? 'Created'
                      : lastActivity.operation === 'DELETE'
                        ? 'Deleted'
                        : lastActivity.change_summary}
                  </Box>{' '}
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    by {lastActivity.user_name || lastActivity.user_email || 'System'}
                  </Box>{' '}
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    on{' '}
                    {new Date(lastActivity.audit_timestamp).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Box>
                </Typography>

                {/* Show field changes if this was an update */}
                {lastActivity.operation === 'UPDATE' &&
                  (() => {
                    const changes = getFieldChanges();
                    return changes && changes.length > 0 ? (
                      <Box sx={{ marginTop: '8px' }}>
                        {changes.map((change, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              padding: '8px 12px',
                              bgcolor: 'background.paper',
                              borderRadius: '3px',
                              marginBottom: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              flexWrap: 'wrap',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '12px',
                                fontWeight: 500,
                                minWidth: '120px',
                                flexShrink: 0,
                              }}
                            >
                              {formatFieldChange(change.field)}:
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                flex: 1,
                                minWidth: 0,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontFamily: 'Roboto Mono',
                                  fontSize: '11px',
                                  color: 'error.main',
                                  textDecoration: 'line-through',
                                  flex: '1 1 0',
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                                title={formatValue(change.oldValue)}
                              >
                                {formatValue(change.oldValue)}
                              </Typography>
                              <Typography
                                sx={{ fontSize: '11px', color: 'text.secondary', flexShrink: 0 }}
                              >
                                →
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: 'Roboto Mono',
                                  fontSize: '11px',
                                  color: 'success.main',
                                  fontWeight: 500,
                                  flex: '1 1 0',
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                                title={formatValue(change.newValue)}
                              >
                                {formatValue(change.newValue)}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ) : null;
                  })()}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              marginTop: '16px',
              paddingTop: '16px',
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '14px' }}>External Status</InputLabel>
              <Select
                value={externalStatus}
                label="External Status"
                onChange={(e) => handleUpdateExternalStatus(e.target.value)}
                disabled={isViewer || updateEntity.isPending}
                sx={{
                  fontSize: '13px',
                }}
              >
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status} sx={{ fontSize: '13px' }}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Line Items Section */}
      <Accordion
        expanded={expandedSections.lineItems}
        onChange={() => toggleSection('lineItems')}
        sx={{
          boxShadow: 'none',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            padding: '0 24px',
            minHeight: '56px',
            '&.Mui-expanded': { minHeight: '56px' },
            bgcolor: (t) => (t.palette.mode === 'dark' ? 'grey.800' : 'grey.50'),
            borderBottom: expandedSections.lineItems ? '1px solid' : 'none',
            borderColor: 'divider',
          }}
        >
          <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
            Line Items ({lineItems.length}
            {cancelledLineItems.length > 0 ? ` + ${cancelledLineItems.length} canceled` : ''})
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: '16px 24px', bgcolor: 'background.paper' }}>
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{ bgcolor: (t) => (t.palette.mode === 'dark' ? 'grey.900' : 'grey.100') }}
                >
                  <TableCell
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      padding: '12px',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    }}
                  >
                    Date
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      padding: '12px',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    }}
                  >
                    LIN
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      padding: '12px',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    }}
                  >
                    NSN/MFR PN
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      padding: '12px',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    }}
                  >
                    Nomenclature
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      padding: '12px',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    }}
                  >
                    Qty Ordered
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      padding: '12px',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    }}
                  >
                    Line Total
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      padding: '12px',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    }}
                  >
                    Qty Received
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      padding: '12px',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    }}
                  >
                    Due In
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      padding: '12px',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    }}
                  >
                    Date Received
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      padding: '12px',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    }}
                  >
                    Contract Number
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      padding: '12px',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      minWidth: '150px',
                    }}
                  >
                    Notes
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.length === 0 && cancelledLineItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} sx={{ textAlign: 'center', padding: '48px' }}>
                      <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                        No line items found for this order
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {lineItems.map((lineItem, index) => {
                      const quantity = lineItem.entity_data.quantity ?? 0;
                      const unitPrice = lineItem.entity_data.unit_price_at_time_of_order ?? 0;
                      const actualPrice = lineItem.entity_data.actual_price ?? unitPrice;
                      const lineTotal = quantity * actualPrice;
                      const qtyReceived = getTotalReceived(lineItem);
                      const dueIn = quantity - qtyReceived;
                      const lastReceipt = lineItem.receipts?.[lineItem.receipts.length - 1];
                      const lastReceiptDate = lastReceipt?.entity_data?.receipt_date;
                      const orderDate = order.orderDate
                        ? new Date(order.orderDate).toLocaleDateString('en-US')
                        : '-';
                      const dateReceived = lastReceiptDate
                        ? new Date(lastReceiptDate).toLocaleDateString('en-US')
                        : '—';
                      const notes = lineItem.entity_data.notes ?? '';
                      const notesEditProps = editableProps(
                        () => handleOpenNotesModal(lineItem.id, notes),
                        'Edit notes',
                        { display: 'flex', alignItems: 'center', gap: 1 },
                      );
                      const contractNumber = lineItem.entity_data.contract_number;

                      return (
                        <TableRow
                          key={lineItem.id}
                          sx={{
                            bgcolor: (t) =>
                              index % 2 === 0
                                ? 'background.paper'
                                : t.palette.mode === 'dark'
                                  ? 'grey.800'
                                  : 'grey.50',
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          {/* Date */}
                          <TableCell
                            sx={{
                              padding: '12px',
                              verticalAlign: 'top',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Typography sx={{ fontSize: '13px', fontWeight: 400 }}>
                              {orderDate}
                            </Typography>
                          </TableCell>
                          {/* LIN */}
                          <TableCell
                            sx={{
                              padding: '12px',
                              verticalAlign: 'top',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Typography sx={{ fontSize: '13px', fontWeight: 400 }}>
                              {lineItem.catalog?.entity_data?.lin || '-'}
                            </Typography>
                          </TableCell>
                          {/* NSN/MFR PN */}
                          <TableCell
                            sx={{
                              padding: '12px',
                              verticalAlign: 'top',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Typography sx={{ fontSize: '13px', fontWeight: 400 }}>
                              {lineItem.catalog?.entity_data?.nsn || '-'}
                            </Typography>
                          </TableCell>
                          {/* Nomenclature */}
                          <TableCell
                            sx={{
                              padding: '12px',
                              maxWidth: '250px',
                              verticalAlign: 'top',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <Typography
                                sx={{
                                  fontSize: '13px',
                                  fontWeight: 400,
                                  lineHeight: '1.5',
                                  whiteSpace: 'normal',
                                }}
                              >
                                {formatNomenclatureWithSize(
                                  lineItem.catalog?.entity_data?.nomenclature ||
                                    lineItem.catalog?.display_name ||
                                    lineItem.display_name ||
                                    '-',
                                  lineItem.catalog?.entity_data?.size as string | undefined,
                                )}
                              </Typography>
                              {(lineItem.entity_data.qty_cancelled ?? 0) > 0 && (
                                <Tooltip
                                  title={`${lineItem.entity_data.qty_cancelled} unit(s) were partially cancelled from this line item`}
                                >
                                  <Chip
                                    label={`−${lineItem.entity_data.qty_cancelled} cancelled`}
                                    size="small"
                                    sx={{
                                      bgcolor: 'warning.main',
                                      color: 'common.white',
                                      fontSize: '10px',
                                      fontWeight: 600,
                                      height: '18px',
                                      alignSelf: 'flex-start',
                                      cursor: 'help',
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          {/* Qty Ordered */}
                          <TableCell
                            align="right"
                            sx={{
                              padding: '12px',
                              verticalAlign: 'top',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Typography
                              sx={{ fontFamily: 'Roboto Mono', fontSize: '13px', fontWeight: 400 }}
                            >
                              {quantity}
                            </Typography>
                          </TableCell>
                          {/* Line Total */}
                          <TableCell
                            align="right"
                            sx={{
                              padding: '12px',
                              verticalAlign: 'top',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Typography
                              sx={{ fontFamily: 'Roboto Mono', fontSize: '13px', fontWeight: 500 }}
                            >
                              {formatCurrency(lineTotal)}
                            </Typography>
                          </TableCell>
                          {/* Qty Received */}
                          <TableCell
                            align="right"
                            sx={{
                              padding: '12px',
                              verticalAlign: 'top',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            {lineItem.receipts && lineItem.receipts.length > 0 ? (
                              <Tooltip
                                title={
                                  <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                      Receipt History
                                    </Typography>
                                    {lineItem.receipts?.map((receipt, idx) => (
                                      <Box
                                        key={idx}
                                        sx={{
                                          mt: 0.5,
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          columnGap: 2,
                                        }}
                                      >
                                        <Typography variant="caption">
                                          {receipt.entity_data?.quantity_received ?? 0} received
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontVariantNumeric: 'tabular-nums',
                                          }}
                                        >
                                          {receipt.entity_data?.receipt_date
                                            ? dayjs(receipt.entity_data.receipt_date).format(
                                                'MM/DD/YYYY',
                                              )
                                            : '-'}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                }
                                arrow
                              >
                                <Chip
                                  size="small"
                                  label={`${qtyReceived} / ${quantity}`}
                                  color={qtyReceived >= quantity ? 'success' : 'warning'}
                                  variant="outlined"
                                  sx={{ cursor: 'help' }}
                                />
                              </Tooltip>
                            ) : (
                              <Typography
                                sx={{
                                  fontFamily: 'Roboto Mono',
                                  fontSize: '13px',
                                  fontWeight: 400,
                                  color: 'text.secondary',
                                }}
                              >
                                0 / {quantity}
                              </Typography>
                            )}
                          </TableCell>
                          {/* Due In */}
                          <TableCell
                            align="right"
                            sx={{
                              padding: '12px',
                              verticalAlign: 'top',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: 'Roboto Mono',
                                fontSize: '13px',
                                fontWeight: 500,
                                color: dueIn > 0 ? 'warning.main' : 'success.main',
                              }}
                            >
                              {dueIn}
                            </Typography>
                          </TableCell>
                          {/* Date Received */}
                          <TableCell
                            sx={{
                              padding: '12px',
                              verticalAlign: 'top',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Typography sx={{ fontSize: '13px', fontWeight: 400 }}>
                              {dateReceived}
                            </Typography>
                          </TableCell>
                          {/* Contract Number (editable) */}
                          <TableCell
                            sx={{
                              padding: '12px',
                              verticalAlign: 'top',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            {renderEditableCell(lineItem, 'contract_number', contractNumber)}
                          </TableCell>
                          {/* Notes (editable with modal) */}
                          <TableCell
                            sx={{
                              padding: '12px',
                              maxWidth: '200px',
                              verticalAlign: 'top',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <Box {...notesEditProps}>
                                <Typography
                                  sx={{
                                    fontSize: '13px',
                                    fontWeight: 400,
                                    whiteSpace: 'normal',
                                    maxWidth: '150px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {notes || '—'}
                                </Typography>
                                <EditIcon
                                  className="edit-icon"
                                  sx={{
                                    fontSize: '14px',
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                    color: 'primary.main',
                                  }}
                                />
                              </Box>
                              {notes && (
                                <Tooltip title={notes}>
                                  <NotesIcon
                                    sx={{
                                      fontSize: '16px',
                                      color: 'primary.main',
                                      marginTop: '2px',
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {cancelledLineItems.map((lineItem, index) => (
                      <CancelledLineItemRow
                        key={lineItem.id}
                        lineItem={lineItem}
                        rowIndex={index}
                        activeRowCount={lineItems.length}
                      />
                    ))}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Notes Modal */}
      <Dialog open={notesModalOpen} onClose={handleCloseNotesModal} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '18px',
            fontWeight: 500,
            borderBottom: '1px solid',
            borderColor: 'divider',
            padding: '16px 24px',
          }}
        >
          Edit Notes
          <IconButton
            onClick={handleCloseNotesModal}
            aria-label="Close"
            sx={{
              color: 'text.secondary',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: '24px' }}>
          <TextField
            multiline
            rows={6}
            fullWidth
            value={notesValue}
            onChange={(e) => setNotesValue(e.target.value)}
            placeholder="Enter notes here..."
            sx={{
              '& .MuiInputBase-root': {
                fontSize: '14px',
              },
              '& .MuiInputBase-input': {
                fontSize: '14px',
              },
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{ borderTop: '1px solid', borderColor: 'divider', padding: '16px 24px' }}
        >
          <Button onClick={handleCloseNotesModal} variant="outlined">
            Cancel
          </Button>
          <ViewerGuardedButton
            onClick={handleSaveNotes}
            variant="contained"
            disabled={updateEntity.isPending}
          >
            Save
          </ViewerGuardedButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
